/**
 * Companies and company members API
 */

import { apiGet, apiPost, apiPut, apiDelete } from './api';

export type CompanyScopeMode = 'active' | 'all_companies';
const COMPANY_SCOPE_STORAGE_KEY = 'gardenhub:company-scope-mode';

/** Same-tab pages (Tasks, Schedule, …) listen to refetch when workspace scope switches off "all companies". */
export const COMPANY_WORKSPACE_CHANGED_EVENT = 'gardenhub:companyWorkspace';

export function notifyCompanyWorkspaceChanged(): void {
	if (typeof window !== 'undefined') {
		window.dispatchEvent(new CustomEvent(COMPANY_WORKSPACE_CHANGED_EVENT));
	}
}

export type Company = {
	id: number;
	name: string;
	created_at?: string;
	country_code?: string | null;
	working_days?: number[] | null;
	non_working_dates?: CompanyNonWorkingDate[];
};

export type CompanyNonWorkingDate = {
	id: number;
	date: string;
	label: string | null;
};

export type CompanySchedule = {
	working_days: number[] | null;
	non_working_dates: CompanyNonWorkingDate[];
};

export type CompanyMember = {
	company_member_id?: number;
	id: number;
	name: string;
	email: string;
	company_name?: string;
	member_role: 'owner' | 'admin' | 'member';
	user_role?: 'admin' | 'company' | 'gardener' | 'seeker';
	can_send_marketplace_offers?: number;
};

export type PendingRequest = {
	id: number;
	user_id: number;
	requested_at: string;
	name: string;
	email: string;
};

export type PendingInvite = {
	id: number;
	company_id: number;
	company_name: string;
	responded_at?: string;
};

export type MyCompanyResponse = {
	company: Company | null;
	activeCompanyId?: number | null;
	companyIds?: number[];
	pendingRequests: PendingRequest[];
	pendingInvites: PendingInvite[];
};

export type CompanyContextsResponse = {
	active_company_id: number | null;
	companies: Company[];
};

export type MyCompanyMembersResponse = {
	members: CompanyMember[];
	company: Company | null;
};

const userIdParam = (userId: string) => `userId=${encodeURIComponent(userId)}`;

/** PDO / JSON often sends 0/1 as strings; JavaScript !!'0' is true, so never use !! for this flag. */
export function isCanSendMarketplaceOffersOn(value: unknown): boolean {
	const n = Number(value);
	return n === 1;
}

export async function listCompanies(): Promise<Company[]> {
	return apiGet<Company[]>('/companies');
}

export async function getMyCompany(userId: string, activeCompanyId?: number | null): Promise<MyCompanyResponse> {
	const activePart = activeCompanyId ? `&activeCompanyId=${activeCompanyId}` : '';
	return apiGet<MyCompanyResponse>(`/companies/my?${userIdParam(userId)}${activePart}`);
}

export async function getMyCompanyContexts(userId: string): Promise<CompanyContextsResponse> {
	return apiGet<CompanyContextsResponse>(`/companies/my/contexts?${userIdParam(userId)}`);
}

export async function setMyActiveCompany(userId: string, companyId: number): Promise<{ active_company_id: number }> {
	return apiPost<{ active_company_id: number }>(`/companies/my/active?${userIdParam(userId)}`, { userId, companyId });
}

export function getStoredCompanyScopeMode(): CompanyScopeMode {
	if (typeof window === 'undefined') return 'active';
	const raw = window.localStorage.getItem(COMPANY_SCOPE_STORAGE_KEY);
	return raw === 'all_companies' ? 'all_companies' : 'active';
}

export function setStoredCompanyScopeMode(mode: CompanyScopeMode): void {
	if (typeof window === 'undefined') return;
	window.localStorage.setItem(COMPANY_SCOPE_STORAGE_KEY, mode);
}

export async function getMyCompanyMembers(userId: string, companyId?: number): Promise<MyCompanyMembersResponse> {
	const q = companyId ? `?${userIdParam(userId)}&companyId=${companyId}` : `?${userIdParam(userId)}`;
	return apiGet<MyCompanyMembersResponse>(`/companies/my/members${q}`);
}

export async function getMyOffersPermission(userId: string): Promise<{ can_send_marketplace_offers: boolean }> {
	const raw = await apiGet<{ can_send_marketplace_offers: boolean | number | string }>(
		`/companies/my/offers-permission?${userIdParam(userId)}`
	);
	return { can_send_marketplace_offers: isCanSendMarketplaceOffersOn(raw.can_send_marketplace_offers) };
}

export async function setCompanyMemberOffersPermission(
	actingUserId: string,
	companyMemberId: number,
	enabled: boolean
): Promise<{ message: string; can_send_marketplace_offers: number }> {
	return apiPost<{ message: string; can_send_marketplace_offers: number }>(
		`/company-members/${companyMemberId}/offers-permission?${userIdParam(actingUserId)}`,
		{ userId: actingUserId, enabled }
	);
}

export async function createCompany(userId: string, name: string): Promise<Company> {
	return apiPost<Company>('/companies', { userId, name });
}

export async function updateMyCompanyProfile(
	userId: string,
	payload: { countryCode: string | null }
): Promise<Company> {
	return apiPut<Company>(`/companies/my?${userIdParam(userId)}`, payload);
}

export async function requestJoinCompany(userId: string, companyId: number): Promise<{ message: string }> {
	return apiPost<{ message: string }>('/companies/request-join', { userId, companyId });
}

export async function inviteToCompany(actingUserId: string, companyId: number, gardenerUserId: number): Promise<{ message: string }> {
	return apiPost<{ message: string }>(`/companies/${companyId}/invite?userId=${encodeURIComponent(actingUserId)}`, { inviteUserId: gardenerUserId });
}

export async function approveCompanyMember(userId: string, memberId: number): Promise<{ message: string }> {
	return apiPost<{ message: string }>(`/company-members/${memberId}/approve`, { userId });
}

export async function rejectCompanyMember(userId: string, memberId: number): Promise<{ message: string }> {
	return apiPost<{ message: string }>(`/company-members/${memberId}/reject`, { userId });
}

export async function acceptCompanyInvite(userId: string, memberId: number): Promise<{ message: string; company?: Company }> {
	return apiPost<{ message: string; company?: Company }>(`/company-members/${memberId}/accept`, { userId });
}

export async function declineCompanyInvite(userId: string, memberId: number): Promise<{ message: string }> {
	return apiPost<{ message: string }>(`/company-members/${memberId}/decline`, { userId });
}

export async function leaveCompany(userId: string, companyId?: number): Promise<{ message: string }> {
	const payload =
		companyId != null && Number.isFinite(companyId)
			? { userId, companyId }
			: { userId };
	return apiDelete<{ message: string }>(`/companies/leave?${userIdParam(userId)}`, payload);
}

export async function getCompanySchedule(userId: string): Promise<CompanySchedule> {
	return apiGet<CompanySchedule>(`/companies/my/schedule?${userIdParam(userId)}`);
}

export async function updateCompanySchedule(
	userId: string,
	payload: { working_days: number[] | null; non_working_dates: { date: string; label?: string | null }[] }
): Promise<CompanySchedule> {
	return apiPut<CompanySchedule>(`/companies/my/schedule?${userIdParam(userId)}`, payload);
}
